import { useSWR } from '@tiger/swr';
import { Button, Form, Popover } from 'antd';
import { isUndefined } from 'lodash';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { EasyField, useForm, withForm } from 'react-formutil';
import { Link, useNavigate } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';
import InvalidRedirect from 'app/components/InvalidRedirect';
import Toast from 'app/components/Toast';
import APP from 'app/stores/APP';
import { CategoryType } from 'app/types/user';
import BottomBox from 'components/BottomBox';
import CurrencyAmount from 'components/CurrencyAmount';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import { PlaidBankCardItem } from 'components/vendor/plaid';
import security from 'stores/security';
import API from 'utils/API';
import { getCurrencyInfo } from 'utils/util';
import AddBank from '../../../../components/AddBank';
import DepositPeriodSelect, { PeriodTypeEnum } from '../DepositPeriodSelect';
import { getAccountLabel } from './helper';
import styles from './style.scss';

export interface RecurringConfigData {
    amount: number;
    period_type: PeriodTypeEnum;
    ach_card_id: number;
    currency?: string;
    source?: 'Fund' | 'Bank';
    deposit_customer_id?: number;
    customer_id?: number;
}

export interface RecurringConfigProps {
    isIRA: boolean;
    isEdit?: boolean;
    subTitle?: ReactNode;
    initialValues: Partial<RecurringConfigData>;
    bankCardList: PlaidBankCardItem[] | undefined;
    onSubmit?: (formData: RecurringConfigData) => Promise<unknown>;
}

const SUPPORT_CURRENCY = 'USD';

/**
 * 定时入金页面，区分是否是 IRA 账户
 * 若是，则所有“Deposit”单词因合规要求，必须使用 Contribution 替换
 * 两个入口：
 * 1. 入金页面，携带入金页面的表单数据（若账户、卡号、金额等作为默认值）
 * 2. 编辑入口，当路径中的 id 存在时表示修改该定时计划，根据 id 查询具体数据
 */
const RecurringConfig: FC<RecurringConfigProps> = ({
    isIRA,
    isEdit,
    subTitle,
    bankCardList,
    initialValues,
    onSubmit
}) => {
    const navigate = useNavigate();
    // 支持币种列表
    const [currencyList] = useState(() => {
        return [SUPPORT_CURRENCY].map(currency => {
            const currencyInfo = getCurrencyInfo(currency);

            return {
                label: currencyInfo.name,
                icon: currencyInfo.icon,
                value: currency
            };
        });
    });

    const bankCardOptions = useMemo(() => {
        return (bankCardList ?? [])
            .filter(({ status, id_source }) => {
                // id_source 分 plaid 和 bank 两种
                // plaid 银行卡全部展示， Bank 为手动认证银行卡，只展示可用的
                return id_source === 'Bank' ? status === 'AVAILABLE' : true;
            })
            .map(item => {
                const acountAbbr = item.account ? `(${item.account.slice(-4)})` : '';

                return {
                    label: `${item.institution_name}${acountAbbr}`,
                    value: item.id
                };
            });
    }, [bankCardList]);

    // 可入金账户列表
    const accountList = useMemo(() => {
        const accounts = (security.switchAccountList ?? []).filter(account => {
            const { accountId, status, category } = account;

            const categoryType: CategoryType = isIRA ? 'IRA' : 'Individual';

            return accountId && ['OPENED', 'FUNDED'].includes(status) && category === categoryType;
        });

        return accounts.map(account => {
            const { customerId } = account;

            return {
                value: customerId,
                label: getAccountLabel(account),
                customerId
            };
        });
    }, [isIRA]);

    // 入金限额信息
    const { data: depoitLimitInfo } = useSWR<{
        available_deposit_times: number; // 剩余入金次数
        five_day: number; // 剩余可入金金额
        once_deposit: number; // 一次最大入金金额
    }>(
        {
            url: API.TRADE.funding.availableCash(),
            params: {
                customer_id: security.currentCustomerId,
                account_id: security.currentAccountId
            }
        },
        {
            dataChain: data => data.deposit,
            immutable: true
        }
    );

    const { $invalid, $batchState, $params, $getFirstError } = useForm<RecurringConfigData>();

    const handleSubmit = async () => {
        APP.trackEvent('入金', '入金_定时入金', '定时入金提交_点击');

        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            Toast.show($getFirstError());

            return;
        }

        const { amount, period_type, deposit_customer_id, ach_card_id } = $params;

        const bankCardInfo = bankCardList?.find(item => item.id === ach_card_id);

        try {
            Toast.loading(true);

            await onSubmit?.({
                amount,
                period_type,
                deposit_customer_id,
                ach_card_id,
                source: bankCardInfo?.id_source,
                currency: SUPPORT_CURRENCY,
                customer_id: security.currentCustomerId
            });
        } finally {
            Toast.loading(false);
        }
    };

    useEffect(() => {
        APP.trackEvent('入金', '入金_定时入金', '页面曝光');
    }, []);

    // 若没有可入金账户，则跳转回入金页面
    if (!accountList.length) {
        const bankCardInfo = bankCardList?.find(item => item.id === initialValues.ach_card_id);

        return <InvalidRedirect to="/profile/deposit/ach/fill" state={bankCardInfo} errorMsg="No deposit account." />;
    }

    return (
        <Form layout="vertical" className={styles.pageRoot}>
            <h3 className={styles.mainTitle}>{isIRA ? __('Recurring Contribution') : __('Recurring Deposit')}</h3>
            {subTitle}
            <FormItem
                name="ach_card_id"
                required
                label={__('Transfer Funds From')}
                $defaultValue={initialValues.ach_card_id}>
                {bankCardList?.length ? (
                    <FieldSelect
                        disabled={isEdit} // 修改定时入金时不支持修改银行卡
                        renderInComponent
                        data={[
                            ...bankCardOptions,
                            {
                                key: __('Add bank account'),
                                label: __('Add bank account'),
                                value: 'add',
                                extraLabel: <AddBank source="deposit" />
                            }
                        ]}
                    />
                ) : (
                    <Button
                        type="link"
                        className="pl-0"
                        onClick={() => {
                            APP.trackEvent('入金', 'ACH 定时入金', '新增银行账户');

                            navigate('/profile/bankcards/auth/select-way', {
                                state: { source: 'deposit' },
                                replace: true
                            });
                        }}>
                        {__('Add bank account')}
                    </Button>
                )}
            </FormItem>
            {/* 修改定时入金时隐藏账户选择，默认使用当前账户 */}
            {!isEdit && accountList.length > 1 ? (
                <FormItem
                    label={isIRA ? __('Contribution to') : __('Deposit to')}
                    name="deposit_customer_id"
                    required
                    $defaultValue={initialValues.deposit_customer_id || security.currentCustomerId}>
                    <FieldSelect data={accountList} />
                </FormItem>
            ) : (
                <EasyField type="hidden" name="deposit_customer_id" $defaultValue={security.currentCustomerId} />
            )}
            {/* 入金金额 */}
            <FormItem label={isIRA ? __('Contribution Amount') : __('Deposit Amount')}>
                <CurrencyAmount
                    currencyList={currencyList}
                    selectConfig={{
                        disabled: currencyList.length <= 1,
                        name: 'currency',
                        $defaultValue: SUPPORT_CURRENCY,
                        showIcon: false
                    }}
                    inputConfig={{
                        name: 'amount',
                        placeholder: '0.00',
                        $defaultValue: initialValues.amount,
                        checker: value => {
                            if (!value) {
                                return __('Please enter an amount');
                            }

                            if (value <= 0) {
                                return __('The amount you entered is invalid');
                            }

                            const onceDeposit = depoitLimitInfo?.once_deposit;

                            if (!isUndefined(onceDeposit) && onceDeposit < value) {
                                return __('Each ACH deposit cannot exceed $25,000.');
                            }

                            return true;
                        }
                    }}
                    help={__(
                        'Please keep sufficient funds in your bank account, otherwise it may result in rejection or overdraft.'
                    )}
                />
            </FormItem>
            {/* 定时入金周期 */}
            <DepositPeriodSelect
                name="period_type"
                label={
                    <>
                        <label className="mr-2">
                            {isIRA ? __('Recurring Contribution Frequency') : __('Recurring Deposit Frequency')}
                        </label>
                        <Popover
                            trigger="click"
                            content={
                                <div
                                    style={{
                                        maxWidth: '50vw'
                                    }}>
                                    {isIRA
                                        ? __('The frequency that you would like to make this contribution.')
                                        : __('The frequency that you would like to make this deposit.')}
                                </div>
                            }
                            placement="right">
                            <QuestionCircleOutlined />
                        </Popover>
                    </>
                }
                isIRA={isIRA}
                defaultValue={initialValues.period_type ?? PeriodTypeEnum.WEEK}
            />
            <BottomBox className="btn-box text-center mt-5">
                <Button type="primary" block size="large" onClick={handleSubmit}>
                    {isEdit ? (isIRA ? __('Edit Recurring Contribution') : __('Edit Recurring Deposit')) : __('Submit')}
                </Button>
                <Link className="d-block mt-3" to="/profile/deposit/ach/recurring/schedules">
                    {isIRA ? __('Recurring Contribution Records') : __('Recurring Deposit Records')}
                </Link>
            </BottomBox>
        </Form>
    );
};

export default withForm(RecurringConfig);
